import React, {useEffect, useState} from 'react';
import {useParams, Link} from 'react-router-dom';

import {Flow, FlowFetchResponse} from '../_services/Flow';
import {DataTableColumn, ExportEntriesState} from '../types';

import {DataTable} from '../components/DataTable';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {Paginator} from '../components/Paginator';
import {SearchAvailableColumn} from '../components/CustomizeColumnsModal';
import {ExportEntriesModal} from '../components/ExportEntriesModal';
import {Cell} from '../components/Cell';
import {WorkflowSearchFormFieldFilter} from '../components/WorkflowSearchFormFieldFilter';
import ReactSelect from 'react-select';

type DataFormPageProps = {};

type SearchResultsData = {
    entries: any;
    columns: DataTableColumn[];
    total_count: number;
    page_size: number;
    page_count: number;
    page: number;
    availableColumns: SearchAvailableColumn[];
};

const initialExportEntriesState: ExportEntriesState = {
    show: false,
    exportId: '',
    errorMessage: '',
};

export const DataForm: React.FunctionComponent<DataFormPageProps> = ({}) => {
    const {workflowId, dataformId} = useParams();

    const [dataforms, setDataforms] = useState<any>([]);
    const [dataform, setDataform] = useState<any>();
    const [dataformIsLoading, setDataformIsLoading] = useState<boolean>(false);
    const [searchFilters, setSearchFilters] = useState<any>({workflow: workflowId, dataformId: dataformId, fieldFilters: [{}]});
    const [searchResults, setSearchResults] = useState<SearchResultsData>({} as SearchResultsData);
    const [searchIsLoading, setSearchIsLoading]: any = useState(false);
    const [searchLoadingErrorMessage, setSearchLoadingErrorMessage]: any = useState('');
    const [exportEntries, setExportEntries] = useState<ExportEntriesState>({...initialExportEntriesState});
    const [searchKey, setSearchKey] = useState<number>(1);

    useEffect(() => {
        if (!dataform) {
            return;
        }

        setSearchFilters({workflow: workflowId, dataformId: dataform.id, fieldFilters: [{}]});
        search();
    }, [dataform]);

    useEffect(() => {
        if (!dataform) {
            return;
        }

        setSearchIsLoading(true);
        setSearchLoadingErrorMessage('');

        Flow.getInstance().dataform(workflowId, dataform.id, searchFilters)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json) {
                    console.log(response.json);
                    setSearchResults(response.json);
                } else {
                    setSearchLoadingErrorMessage(response.json.message || 'Error performing search');
                }
            })
            .catch((error: any) => {
                setSearchLoadingErrorMessage(error || 'Exception performing search');
            })
            .finally(() => {
                setSearchIsLoading(false);
            })

    }, [searchKey]);

    useEffect(() => {
        setDataformIsLoading(true);

        Flow.getInstance().getForm(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    if (response.json.form?.customDataforms?.length > 0) {
                        const dataforms = response.json.form.customDataforms.map((d: any) => ({
                            ...d,
                            text: d.title,
                            label: d.title,
                            value: d.id
                        }))

                        setDataforms(dataforms);

                        const passedDataForm = dataforms.find((d: any) => d.id === dataformId);
                        if (passedDataForm) {
                            setDataform(passedDataForm);
                        } else {
                            setDataform(dataforms[0]);
                        }

                    }
                } else {
                    throw Error(response?.json?.message || 'Error getting Workflow');
                }
            })
            .catch((error: any) => {
                console.log(error);
            })
            .finally(() => {
                setDataformIsLoading(false);
            })
    }, [workflowId]);

    const handleExport = () => {
        setExportEntries(() => ({
            ...initialExportEntriesState,
            show: true,
        }));

        Flow.getInstance().export(searchFilters, null)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setExportEntries((state: ExportEntriesState) => ({
                        ...state,
                        exportId: response.json.exportId
                    }));
                } else {
                    throw Error(response.json.message || 'Error exporting records. Close modal and try again');
                }
            })
            .catch((error: any) => {
                setExportEntries((state: ExportEntriesState) => ({
                    ...state,
                    errorMessage: error?.message || error || 'Error exporting records. Close modal and try again'
                }));
            });
    };

    const search = () => setSearchKey(searchKey + 1);

    return (
        <div>
            <div className="d-flex pb-2 justify-content-between">
                {dataform && <h1 className="flex-grow-1">{dataform.title}</h1>}
                {dataforms?.length > 1 && <div style={{width: '300px'}}>
                    <ReactSelect
                        required={true}
                        onChange={(option) => {
                            setDataform(option);
                        }}
                        options={dataforms}
                        defaultValue={null}
                        value={dataform}
                        placeholder="Select a Dataform"
                    />
                </div>}
            </div>

            <div className="dataform-filters p-2" id="workflow-search-form">
                {searchFilters.fieldFilters.map((fieldFilter: any, index: number) => (
                    <WorkflowSearchFormFieldFilter
                        key={index}
                        // @ts-ignore
                        filters={dataform?.columns?.filter((col: any) => col.hasOwnProperty('operators'))
                            .map((col: any) => ({
                            field: {...col},
                            key: col.prop,
                            text: col.label
                        }))}
                        operators={fieldFilter?.workflowField?.field?.operators}
                        values={fieldFilter?.workflowField?.field?.options?.map((opt: any) => ({
                            key: opt,
                            text: opt
                        }))}
                        filterValues={fieldFilter}
                        onChange={(field: string, value: any) => {
                            setSearchFilters({
                                ...searchFilters,
                                fieldFilters: [
                                    ...searchFilters.fieldFilters.slice(0, index),
                                    {
                                        ...searchFilters.fieldFilters[index],
                                        [field]: value
                                    },
                                    ...searchFilters.fieldFilters.slice(index + 1)
                                ],
                            });
                        }}
                    />
                ))}
                <div className="mt-2 d-flex justify-content-between">
                    {searchFilters.fieldFilters.length > 0 ?
                        <button type="button"
                                className="btn btn-primary"
                                disabled={searchIsLoading}
                                onClick={search}
                        >Search</button>
                        :
                        <div/>
                    }
                    <button
                        type="button"
                        className="btn btn-light text-muted"
                        onClick={() => {
                            setSearchFilters({
                                ...searchFilters,
                                fieldFilters: [
                                    ...searchFilters.fieldFilters,
                                    {}
                                ]
                            })
                        }}
                    >
                        <i className="fas fa-plus"/>
                        <span className="pl-3">Add Filter</span>
                    </button>
                </div>
            </div>

            {searchResults && searchResults?.entries?.length > 0 &&
            <div className="row">
                <div className="col-12 pt-2 text-right">
                    <button type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                handleExport();
                            }}
                    >Export
                    </button>
                </div>
            </div>
            }

            {searchLoadingErrorMessage && <p className="alert alert-danger">{searchLoadingErrorMessage}</p>}
            {(dataformIsLoading || searchIsLoading) && <LoadingIndicator/>}

            {dataform?.columns?.length > 0 && searchResults.entries &&
            <div>
                <DataTable
                    id="search-table"
                    striped={true}
                    responsive={true}
                    classNames="table-borderless"
                    headings={dataform.columns}
                    rows={searchResults.entries}
                    cellRenderers={{
                        id: (id: any, value: any) => (
                            <Cell
                                key={id}
                                content={
                                    <Link to={`/entry/${value}`}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                          }}
                                    >{value}</Link>
                                }
                                classNames={`cell-${id} cell-id`}
                            />
                        )
                    }}
                >
                    {searchResults.entries.length > 0 &&
                    <div className="d-flex justify-content-between align-items-center">
                        <Paginator currentPage={searchResults.page}
                                   rowsPerPage={searchResults.page_size}
                                   numberOfPages={searchResults.page_count}
                                   numberOfRecords={searchResults.total_count}
                                   showTotalRecordCount={true}
                                   onChange={(page: number, recordsPerPage: number) => {
                                       setSearchFilters({...searchFilters, page, page_size: recordsPerPage});
                                       setSearchResults({...searchResults, entries: []});
                                       search();
                                   }}
                        />
                    </div>
                    }
                </DataTable>

                {exportEntries.show &&
                <ExportEntriesModal
                    exportId={exportEntries.exportId}
                    errorMessage={exportEntries.errorMessage}
                    close={() => {
                        setExportEntries({
                            ...initialExportEntriesState
                        });
                    }}
                />
                }
            </div>
            }
        </div>
    );
};
